import { useCallback } from 'react';
import { useChatStore } from '@/features/chats/stores/chat.store';
import { getModelCapability } from '@inference.engine/model-registry/web';

interface RetryArgs {
  provider: string;
  modelKey: string;
}

/**
 * Hook that resubmits the last user prompt with a different model. Used by
 * the provider-fallback retry buttons rendered in ErrorMetadata: when the
 * user clicks "Retry with Gemini", we update the chat input options to the
 * requested model and re-invoke `sendMessage` with the same prompt, files,
 * and inputs as the previous attempt.
 *
 * No-op when no prior user message exists, when no current agent is active,
 * or when the model registry does not know the requested modelKey.
 */
export function useRetryWithProvider() {
  const sendMessage = useChatStore((s) => s.sendMessage);
  const setInputOptions = useChatStore((s) => s.setInputOptions);
  const messages = useChatStore((s) => s.messages);
  const currentAgentId = useChatStore((s) => s.currentAgentId);
  const inputOptions = useChatStore((s) => s.chatInput.options);

  const retry = useCallback(
    async ({ modelKey }: RetryArgs): Promise<void> => {
      const lastUserMessage = [...messages]
        .reverse()
        .find((m): m is Extract<typeof m, { type: 'user' }> => m.type === 'user');
      if (!lastUserMessage || !currentAgentId) return;

      if (!getModelCapability(modelKey)) return;

      const newLlmOptions = { model: modelKey };

      // Update chatInput.options so the model dropdown reflects the new selection.
      setInputOptions((prev) => ({
        ...prev,
        llmOptions: newLlmOptions,
      }));

      const newInputs = { ...lastUserMessage.inputs, llmOptions: newLlmOptions };

      // sendMessage takes knowledgeBaseIds as a top-level arg (the original send
      // path in useChatInput strips it off chatInput.options before forwarding).
      // Pull it from the store so retries preserve the KBs attached to the
      // previous turn.
      const knowledgeBaseIds = inputOptions?.knowledgeBaseIds as string[] | undefined;

      await sendMessage({
        agentId: currentAgentId,
        prompt: lastUserMessage.content,
        inputs: newInputs,
        files: lastUserMessage.files,
        knowledgeBaseIds,
      });
    },
    [messages, currentAgentId, sendMessage, setInputOptions, inputOptions],
  );

  return { retry };
}
